import AxiosService from '@/services/api/AxiosService';
import ApiLinks from '@/entities/ApiLinks';
import ApiStatus from '@/entities/enums/apiStatuses';
import { PublicDashboard } from '@/entities/models/PublicDashboard';
import { ApiQueries } from '@/entities/ApiQueries';
import { Event } from '@/entities/models/Event';
import { ReportRecord } from '@/entities/models/ReportRecord';
import { Statistics } from '@/entities/Statistics';
import EventType from '@/entities/EventType';
import FileDownloader from '../FileDownloader';
const DEFAULT_PDF_REPORT_FILENAME = 'report.pdf';
export default class PublicDashboardRepository {
    constructor(ctx) {
        this.ctx = ctx;
    }
    async getPublicDashboardData(dashboardId) {
        let url = `${ApiLinks.PublicDashboard.Home}/${dashboardId}`;
        let response = await AxiosService.getDataFromServer(url, this.ctx, false);
        if (response.status != ApiStatus.SUCCESS)
            return response;
        else
            return PublicDashboard.fromApi(response.data.data);
    }
    async getPublicDashboardEventTypes(dashboardId) {
        let url = `${ApiLinks.PublicDashboard.Home}/${dashboardId}${ApiLinks.PublicDashboard.Keys.EventTypes}`;
        let response = await AxiosService.getDataFromServer(url, this.ctx, false);
        if (response.status != ApiStatus.SUCCESS)
            return response;
        else
            return EventType.fromApi(response.data.data);
    }
    async getPublicDashboardMeasurements(dashboardId) {
        let url = `${ApiLinks.PublicDashboard.Home}/${dashboardId}${ApiLinks.PublicDashboard.Keys.Sources}`;
        let response = await AxiosService.getDataFromServer(url, this.ctx);
        if (response.status != ApiStatus.SUCCESS)
            return null;
        else
            return Event.fromApi(response.data.data);
    }
    async getPublicDashboardChart(dashboardId, sourceIds, from) {
        if (sourceIds.length <= 0)
            return {};
        let url = `${ApiLinks.PublicDashboard.Home}/${dashboardId}${ApiLinks.PublicDashboard.Keys.Chart}`;
        let queries = new ApiQueries();
        let sourcesParam = [];
        sourceIds.forEach((id) => {
            sourcesParam.push(`sourceIds[]=${id}`);
        });
        if (from)
            queries.from = from;
        if (queries.hasQueries())
            url += `?${queries.toQueryString()}&${sourcesParam.join('&')}`;
        let response = await AxiosService.getDataFromServer(url, this.ctx);
        if (response.status != ApiStatus.SUCCESS)
            return null;
        else
            return response.data.data;
    }
    async getUptimeChartDataForSources(sourceIds, from, dashboardId, duration = 300) {
        if (sourceIds.length <= 0)
            return {};
        let url = `${ApiLinks.PublicDashboard.Home}/${dashboardId}${ApiLinks.PublicDashboard.Keys.Outage}`;
        let queries = new ApiQueries();
        if (sourceIds)
            queries.sourcesArray = sourceIds;
        if (from)
            queries.from = from;
        if (duration)
            queries.duration = duration;
        if (queries.hasQueries())
            url += `?${queries.toQueryString()}`;
        let response = await AxiosService.getDataFromServer(url, this.ctx);
        if (response.status != ApiStatus.SUCCESS)
            return {};
        else
            return response.data.data;
    }
    async getPublicDashboardReports(dashboardId, sourceIds, annotations, from, to, model, interval, offset, showEmpty, timezone) {
        if (sourceIds.length <= 0)
            return { records: {}, statistics: {} };
        let url = `${ApiLinks.PublicDashboard.Home}/${dashboardId}${ApiLinks.PublicDashboard.Keys.Reports}`;
        let queries = new ApiQueries();
        let sourcesParam = [];
        sourceIds.forEach((id) => {
            sourcesParam.push(`sourceIds[]=${id}`);
        });
        if (from)
            queries.from = from;
        if (to)
            queries.to = to;
        if (annotations)
            queries.annotations = annotations;
        if (model)
            queries.model = model;
        if (interval)
            queries.interval = interval;
        if (offset)
            queries.offset = offset;
        if (!showEmpty)
            queries.showEmpty = showEmpty;
        queries.timezone = timezone;
        if (queries.hasQueries())
            url += `?${queries.toQueryString()}&${sourcesParam.join('&')}`;
        let response = await AxiosService.getDataFromServer(url, this.ctx);
        if (response.status != ApiStatus.SUCCESS)
            return null;
        let rawReportRecords = {};
        let rawStatistics = {};
        Object.keys(response.data.data).forEach((key) => {
            let dataPerSource = response.data.data[key];
            rawReportRecords[key] = dataPerSource.data;
            rawStatistics[key] = dataPerSource.statistics;
        });
        let parsedRecords = ReportRecord.fromApi(rawReportRecords);
        let parsedStatistics = Statistics.fromApi(rawStatistics);
        return { records: parsedRecords || {}, statistics: parsedStatistics || {} };
    }
    async createPdfReport(config, dashboardId) {
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.PublicDashboard.Home}/${dashboardId}${ApiLinks.PublicDashboard.Keys.Sources}${ApiLinks.PublicDashboard.Keys.ExportPdf}`;
        let data = new FormData();
        data.set('from', config.from);
        data.set('to', config.to);
        data.set('timezone', config.timezone);
        data.set('timeFormat', config.timeFormat);
        data.set('locale', config.locale);
        data.set('showChart', config.showChart.toString());
        data.set('showAnnotationTable', config.showAnnotationTable.toString());
        config.types.forEach((type) => {
            data.append('types[]', type);
        });
        data.set('showRecordsTable', config.showRecordsTable);
        data.set('alarmOnly', config.alarmOnly.toString());
        data.set('models[]', config.models);
        if (config.offset) {
            data.set('offset', config.offset.toString());
        }
        data.set('interval', config.interval.toString());
        config.sourceIds.forEach((id) => {
            data.append('sourceIds[]', id);
        });
        data.set('showEmpty', config.showEmpty.toString());
        data.set('showStatistics', config.showStatistics.toString());
        config.crop.forEach((crop, index) => {
            data.set(`crop[${index}].serviceId`, crop.serviceId.toString());
            data.set(`crop[${index}].min`, crop.min.toString());
            data.set(`crop[${index}].max`, crop.max.toString());
        });
        if (config.boundaries) {
            config.boundaries.forEach((boundary, index) => {
                data.set(`boundaries[${index}].customLowerValue`, boundary.CustomLowerValue.toString());
                data.set(`boundaries[${index}].customUpperValue`, boundary.CustomUpperValue.toString());
                data.set(`boundaries[${index}].eventType`, boundary.EventType.toString());
            });
        }
        let response = await AxiosService.downloadFile_POST(url, data, this.ctx);
        if (response.status == ApiStatus.SUCCESS) {
            FileDownloader.downloadFile(response, DEFAULT_PDF_REPORT_FILENAME);
            return true;
        }
        else
            return false;
    }
    async createOnePagePdfReport(config, dashboardId) {
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.PublicDashboard.Home}/${dashboardId}${ApiLinks.PublicDashboard.Keys.Sources}${ApiLinks.PublicDashboard.Keys.ExportOnePagePdf}`;
        let data = new FormData();
        data.set('from', config.from);
        data.set('to', config.to);
        data.set('timezone', config.timezone);
        data.set('timeFormat', config.timeFormat);
        data.set('locale', config.locale);
        data.set('showChart', 'true');
        config.types.forEach((type) => {
            data.append('types[]', type);
        });
        data.set('models[]', config.models);
        if (config.offset) {
            data.set('offset', config.offset.toString());
        }
        data.set('interval', config.interval.toString());
        data.set('showEmpty', config.showEmpty.toString());
        data.set('showStatistics', config.showStatistics.toString());
        config.sourceIds.forEach((id) => {
            data.append('sourceIds[]', id);
        });
        config.crop.forEach((crop, index) => {
            data.set(`crop[${index}].serviceId`, crop.serviceId.toString());
            data.set(`crop[${index}].min`, crop.min.toString());
            data.set(`crop[${index}].max`, crop.max.toString());
        });
        if (config.boundaries) {
            config.boundaries.forEach((boundary, index) => {
                data.set(`boundaries[${index}].customLowerValue`, boundary.CustomLowerValue.toString());
                data.set(`boundaries[${index}].customUpperValue`, boundary.CustomUpperValue.toString());
                data.set(`boundaries[${index}].eventType`, boundary.EventType.toString());
            });
        }
        let response = await AxiosService.downloadFile_POST(url, data, this.ctx);
        if (response.status == ApiStatus.SUCCESS) {
            FileDownloader.downloadFile(response, DEFAULT_PDF_REPORT_FILENAME);
            return true;
        }
        else
            return false;
    }
    async createRawReport(config, dashboardId) {
        // eslint-disable-next-line vue/max-len
        let url = `${ApiLinks.PublicDashboard.Home}/${dashboardId}${ApiLinks.PublicDashboard.Keys.Sources}${ApiLinks.PublicDashboard.Keys.ExportRawPdf}`;
        let data = new FormData();
        data.set('from', config.from);
        data.set('to', config.to);
        data.set('timezone', config.timezone);
        data.set('timeFormat', config.timeFormat);
        data.set('locale', config.locale);
        config.types.forEach((type) => {
            data.append('types[]', type);
        });
        config.sourceIds.forEach((id) => {
            data.append('sourceIds[]', id);
        });
        let response = await AxiosService.downloadFile_POST(url, data, this.ctx);
        if (response.status == ApiStatus.SUCCESS) {
            FileDownloader.downloadFile(response, DEFAULT_PDF_REPORT_FILENAME);
            return true;
        }
        else
            return false;
    }
}
